import React, { createContext, useContext, useCallback, PropsWithChildren } from 'react';
import { tripInvitesService, AcceptInviteResult } from '../services/supabase/tripInvites.service';

interface InvitesContextType {
  acceptInviteWithRetry: (inviteCode: string, userId: string) => Promise<AcceptInviteResult>;
}

const InvitesContext = createContext<InvitesContextType | undefined>(undefined);

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000; // 1 second between retries

export const InvitesProvider: React.FC<PropsWithChildren> = ({ children }) => {
  /**
   * Accept invite with retry mechanism
   * Handles race condition between Firebase auth and Supabase user sync
   * New users may not be synced to Supabase database immediately after Firebase signup
   */
  const acceptInviteWithRetry = useCallback(async (
    inviteCode: string,
    userId: string
  ): Promise<AcceptInviteResult> => {
    let lastError: string | undefined;
    let acceptResult: AcceptInviteResult | null = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      console.log(`Attempting to accept invite (attempt ${attempt}/${MAX_RETRIES})...`);

      // Wait before each attempt (including first one to allow initial sync)
      if (attempt > 1) {
        console.log(`Waiting ${RETRY_DELAY_MS}ms before retry...`);
      }
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));

      acceptResult = await tripInvitesService.acceptInvite(inviteCode, userId);

      if (acceptResult.success) {
        // Success! Break out of retry loop
        console.log('Invite accepted successfully');
        return acceptResult;
      } else {
        lastError = acceptResult.error;
        console.log(`Attempt ${attempt} failed:`, lastError);

        // If it's not a "not found" error, don't retry (e.g., invite expired, already used)
        if (!lastError?.includes('not found') && !lastError?.includes('Invite not found')) {
          console.log('Non-retryable error, stopping retry loop');
          break;
        }
      }
    }

    // All retries exhausted or non-retryable error
    return {
      success: false,
      trip_id: acceptResult?.trip_id || '',
      error: lastError || 'Failed to accept invite',
    };
  }, []);

  const value: InvitesContextType = {
    acceptInviteWithRetry,
  };

  return (
    <InvitesContext.Provider value={value}>
      {children}
    </InvitesContext.Provider>
  );
};

export const useInvites = (): InvitesContextType => {
  const context = useContext(InvitesContext);
  if (!context) {
    throw new Error('useInvites must be used within an InvitesProvider');
  }
  return context;
};
